"use client";

import { c as _c } from "react/compiler-runtime";
import { filterOptionLabelCacheInputKey, formatFilterValueLabel, getFilterOptionLabelCache, getFilterValueLabels, getFilterValueOption, isFilterValueActive, isPendingAcademicScopeValue } from "@/components/filters/filter-values";
import type { FilterValueOption } from "@/components/filters/filter-values";
import { FilterDropdown, FilterLabelHydrator, StandaloneFilterDropdown } from "@/components/filters/FilterDropdown";
import type { FilterOptionsResolvedHandler } from "@/components/filters/FilterDropdown";
import { Button } from "@/components/ui/Button";
import { FilterBar, FilterBarAdd, FilterBarClear } from "@/components/ui/FilterBar";
import { FilterChip, FilterChipField, FilterChipOperator, FilterChipRemove, FilterChipValue } from "@/components/ui/FilterChip";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusDot } from "@/components/ui/StatusDot";
import type { AnyFilterConfig, AnyLabeledFilterConfig } from "@/types/list";
import { getFilterExclusions, moveFilterOperator, setFilterCondition } from "@/utils/negative-list-filtering";
import type { FilterOperator } from "@/utils/negative-list-filtering";
import { ChevronDown, ListFilterIcon } from "lucide-react";
import type { ComponentType } from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import cn from "twsx";
const emptyFilterInput: Record<string, unknown> = {};
const emptyFilterKeys: string[] = [];

/** "Status" → "statuses", "Agency" → "agencies", "Program" → "programs": the noun a collapsed multi-value chip counts. */
export function pluralizeFilterNoun(label: string) {
  const noun = label.toLowerCase();
  if (/[^aeiou]y$/u.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(?:s|x|ch|sh)$/u.test(noun)) return `${noun}es`;
  return `${noun}s`;
}
export function getFilterOperatorLabel(filterConfig: AnyLabeledFilterConfig, valueCount: number, operator: FilterOperator = "is") {
  if (operator === "isNot") return filterConfig.type === "input" ? "does not contain" : valueCount > 1 ? "is none of" : "is not";
  switch (filterConfig.type) {
    case "input":
      {
        return "contains";
      }
    case "multi-select":
      {
        return valueCount > 1 ? "is any of" : "is";
      }
    case "boolean":
    case "date":
    case "select":
      {
        return "is";
      }
    default:
      {
        return "is";
      }
  }
}
export function getActiveFilterClauses(filterConfigs: Record<string, AnyLabeledFilterConfig>, currentFilters: Record<string, unknown> | undefined, filterInput: Record<string, unknown>, hiddenFilterKeys: string[]) {
  if (!currentFilters) {
    return [];
  }
  const hiddenFilterKeySet = new Set(hiddenFilterKeys);
  return Object.entries(filterConfigs).flatMap(([filterKey, filterConfig]) => {
    if (hiddenFilterKeySet.has(filterKey)) {
      return [];
    }
    return (["is", "isNot"] as const).flatMap(operator => {
      const value = operator === "is" ? currentFilters[filterKey] : getFilterExclusions(currentFilters)[filterKey];
      if (!isFilterValueActive(filterConfig, value)) {
        return [];
      }
      const valueLabels = getFilterValueLabels(filterConfig, value, filterInput, filterKey);
      const resolvedValues = Array.isArray(value) ? value : [value];
      const values = resolvedValues.map((resolvedValue, index) => {
        const valueLabel = valueLabels[index] ?? "";
        const option = getFilterValueOption(filterConfig, String(resolvedValue), filterInput, filterKey);
        return {
          key: `${filterKey}:${String(resolvedValue)}`,
          value: resolvedValue,
          valueLabel,
          icon: option?.icon,
          tone: option?.tone,
          pending: isPendingAcademicScopeValue(filterConfig, resolvedValue, filterInput, filterKey)
        };
      }).filter(resolvedValue => resolvedValue.valueLabel);
      if (values.length === 0) {
        return [];
      }
      return [{
        key: operator === "is" ? filterKey : `${filterKey}:isNot`,
        operator,
        filterKey,
        label: filterConfig.label,
        values
      }];
    });
  });
}
export function ListQueryControls(t0) {
  const $ = _c(90);
  const {
    filterConfigs,
    filterComponents,
    filterInput,
    currentFilters,
    onFilterChange,
    showTrigger: t1,
    showActiveClauses: t2,
    showStandaloneFilters: t3,
    hiddenFilterKeys: t4,
    standaloneFilterKeys: t5,
    className,
    triggerLabel: t6,
    wrapWithParent: t7
  } = t0;
  const showTrigger = t1 === undefined ? true : t1;
  const showActiveClauses = t2 === undefined ? true : t2;
  const showStandaloneFilters = t3 === undefined ? true : t3;
  const hiddenFilterKeys = t4 === undefined ? emptyFilterKeys : t4;
  const standaloneFilterKeys = t5 === undefined ? emptyFilterKeys : t5;
  const triggerLabel = t6 === undefined ? "Filter" : t6;
  const wrapWithParent = t7 === undefined ? false : t7;
  const [isInteractive, setIsInteractive] = useState(false);
  const hasFilterConfigs = Object.keys(filterConfigs).length > 0;
  let t8;
  let t9;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsInteractive(true);
    };
    t9 = [];
    $[0] = t8;
    $[1] = t9;
  } else {
    t8 = $[0];
    t9 = $[1];
  }
  useEffect(t8, t9);
  const resolvedFilterInput = filterInput ?? emptyFilterInput;
  let t10;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {};
    $[2] = t10;
  } else {
    t10 = $[2];
  }
  const [optionLabelCache, setOptionLabelCache] = useState(t10);
  let t11;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {};
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const [optionMarkCache, setOptionMarkCache] = useState(t11);
  let t12;
  if ($[4] !== optionLabelCache || $[5] !== resolvedFilterInput) {
    const seededLabelCache = getFilterOptionLabelCache(resolvedFilterInput) ?? {};
    const filterKeys = new Set([...Object.keys(seededLabelCache), ...Object.keys(optionLabelCache)]);
    t12 = {
      ...resolvedFilterInput,
      [filterOptionLabelCacheInputKey]: Object.fromEntries([...filterKeys].map(filterKey => [filterKey, {
        ...seededLabelCache[filterKey],
        ...optionLabelCache[filterKey]
      }]))
    };
    $[4] = optionLabelCache;
    $[5] = resolvedFilterInput;
    $[6] = t12;
  } else {
    t12 = $[6];
  }
  const filterInputWithLabelCache = t12;
  let t13;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (filterKey_0, options) => {
      const markedOptions = options.filter(_temp);
      if (markedOptions.length > 0) {
        setOptionMarkCache(previousCache => ({
          ...previousCache,
          [filterKey_0]: {
            ...previousCache[filterKey_0],
            ...Object.fromEntries(markedOptions.map(_temp2))
          }
        }));
      }
      setOptionLabelCache(previousCache_0 => {
        const previousFilterCache = previousCache_0[filterKey_0] ?? {};
        const nextFilterCache = {
          ...previousFilterCache
        };
        let changed = false;
        options.forEach(option_1 => {
          if (nextFilterCache[option_1.value] !== option_1.label) {
            nextFilterCache[option_1.value] = option_1.label;
            changed = true;
          }
        });
        if (!changed) {
          return previousCache_0;
        }
        return {
          ...previousCache_0,
          [filterKey_0]: nextFilterCache
        };
      });
    };
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const handleOptionsResolved = t13;
  const typedCurrentFilters = currentFilters as Record<string, unknown> | undefined;
  let t14;
  if ($[8] !== hiddenFilterKeys || $[9] !== standaloneFilterKeys) {
    t14 = [...hiddenFilterKeys, ...standaloneFilterKeys];
    $[8] = hiddenFilterKeys;
    $[9] = standaloneFilterKeys;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  const activeHiddenFilterKeys = t14;
  let t15;
  if ($[11] !== activeHiddenFilterKeys || $[12] !== filterConfigs || $[13] !== filterInputWithLabelCache || $[14] !== typedCurrentFilters) {
    t15 = getActiveFilterClauses(filterConfigs, typedCurrentFilters, filterInputWithLabelCache, activeHiddenFilterKeys);
    $[11] = activeHiddenFilterKeys;
    $[12] = filterConfigs;
    $[13] = filterInputWithLabelCache;
    $[14] = typedCurrentFilters;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const activeClauses = t15;
  let t16;
  if ($[16] !== activeClauses || $[17] !== onFilterChange || $[18] !== typedCurrentFilters) {
    t16 = () => {
      let nextFilters = {
        ...(typedCurrentFilters ?? {})
      };
      activeClauses.forEach(clause => {
        nextFilters = setFilterCondition(nextFilters, clause.filterKey, clause.operator, undefined);
      });
      onFilterChange?.(nextFilters as $Filters);
    };
    $[16] = activeClauses;
    $[17] = onFilterChange;
    $[18] = typedCurrentFilters;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const clearActiveClauses = t16;
  let t17;
  if ($[20] !== filterConfigs || $[21] !== onFilterChange || $[22] !== typedCurrentFilters) {
    t17 = values => {
      let next = values;
      for (const key of Object.keys(filterConfigs)) {
        if (values[key] !== typedCurrentFilters?.[key]) {
          next = setFilterCondition(next, key, "is", values[key]);
        }
      }
      onFilterChange?.(next as $Filters);
    };
    $[20] = filterConfigs;
    $[21] = onFilterChange;
    $[22] = typedCurrentFilters;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const handlePositiveChange = t17;
  let t18;
  if ($[24] !== hiddenFilterKeys || $[25] !== standaloneFilterKeys) {
    t18 = new Set([...hiddenFilterKeys, ...standaloneFilterKeys]);
    $[24] = hiddenFilterKeys;
    $[25] = standaloneFilterKeys;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const addFilterHiddenKeySet = t18;
  let t19;
  if ($[27] !== filterComponents || $[28] !== filterConfigs || $[29] !== filterInputWithLabelCache || $[30] !== showStandaloneFilters || $[31] !== standaloneFilterKeys || $[32] !== typedCurrentFilters) {
    t19 = showStandaloneFilters ? standaloneFilterKeys.flatMap(filterKey_1 => {
      const filterConfig = filterConfigs[filterKey_1];
      const filterComponent = filterComponents[filterKey_1];
      if (!filterConfig || !filterComponent) {
        return [];
      }
      const value = typedCurrentFilters?.[filterKey_1];
      const isActive = isFilterValueActive(filterConfig, value);
      const valueLabel = isActive ? formatFilterValueLabel(filterConfig, value, filterInputWithLabelCache, filterKey_1) : "";
      const pending = isActive && (Array.isArray(value) ? value : [value]).some(item => isPendingAcademicScopeValue(filterConfig, item, filterInputWithLabelCache, filterKey_1));
      return [{
        filterKey: filterKey_1,
        filterConfig,
        filterComponent,
        valueLabel,
        pending
      }];
    }) : [];
    $[27] = filterComponents;
    $[28] = filterConfigs;
    $[29] = filterInputWithLabelCache;
    $[30] = showStandaloneFilters;
    $[31] = standaloneFilterKeys;
    $[32] = typedCurrentFilters;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const standaloneFilters = t19;
  let t20;
  if ($[34] !== addFilterHiddenKeySet || $[35] !== filterConfigs) {
    t20 = Object.fromEntries(Object.entries(filterConfigs).filter(t21 => {
      const [filterKey_2] = t21;
      return !addFilterHiddenKeySet.has(filterKey_2);
    }));
    $[34] = addFilterHiddenKeySet;
    $[35] = filterConfigs;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const addFilterConfigs = t20 as Record<string, AnyLabeledFilterConfig>;
  let t21;
  if ($[37] !== addFilterConfigs) {
    t21 = Object.keys(addFilterConfigs);
    $[37] = addFilterConfigs;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const hasAddFilterConfigs = t21.length > 0;
  if (!hasFilterConfigs) {
    return null;
  }
  if (standaloneFilters.length === 0 && !showTrigger && !showActiveClauses) {
    return null;
  }
  let t22;
  if ($[39] !== activeClauses || $[40] !== filterComponents || $[41] !== filterConfigs || $[42] !== filterInputWithLabelCache || $[43] !== showActiveClauses || $[44] !== typedCurrentFilters) {
    t22 = showActiveClauses && activeClauses.map(clause_0 => {
      const filterConfig_0 = filterConfigs[clause_0.filterKey];
      const filterComponent_0 = filterComponents[clause_0.filterKey];
      if (!filterConfig_0?.hydrateSelectedOptionLabels || !filterComponent_0) {
        return null;
      }
      return <FilterLabelHydrator key={`label-hydrator:${clause_0.key}:${clause_0.values.map(_temp3).join("|")}`} filterKey={clause_0.filterKey} filterConfig={filterConfig_0} filterComponent={filterComponent_0} filterInput={filterInputWithLabelCache} currentValues={clause_0.operator === "isNot" ? {
        ...typedCurrentFilters,
        [clause_0.filterKey]: getFilterExclusions(typedCurrentFilters)[clause_0.filterKey]
      } : typedCurrentFilters} onOptionsResolved={handleOptionsResolved} />;
    });
    $[39] = activeClauses;
    $[40] = filterComponents;
    $[41] = filterConfigs;
    $[42] = filterInputWithLabelCache;
    $[43] = showActiveClauses;
    $[44] = typedCurrentFilters;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== filterInputWithLabelCache || $[47] !== handlePositiveChange || $[48] !== standaloneFilters || $[49] !== typedCurrentFilters) {
    let t24;
    if ($[51] !== filterInputWithLabelCache || $[52] !== handlePositiveChange || $[53] !== typedCurrentFilters) {
      t24 = t25 => {
        const {
          filterKey: filterKey_3,
          filterConfig: filterConfig_1,
          filterComponent: filterComponent_1,
          valueLabel: valueLabel_0,
          pending: pending_0
        } = t25;
        return <Fragment key={filterKey_3}>{filterConfig_1.hydrateSelectedOptionLabels && <FilterLabelHydrator filterKey={filterKey_3} filterConfig={filterConfig_1} filterComponent={filterComponent_1} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onOptionsResolved={handleOptionsResolved} />}<StandaloneFilterDropdown filterKey={filterKey_3} filterConfig={filterConfig_1} filterComponent={filterComponent_1} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved}><Button variant="tertiary" size="lg" type="button" aria-label={`Change ${filterConfig_1.label}`} tooltip={`Change ${filterConfig_1.label}`} className="min-w-0 max-w-64" data-list-filter-scope="true">{filterConfig_1.icon}<span className={cn("shrink-0", valueLabel_0 && "list-filter-scope-label max-md:hidden")}>{`${filterConfig_1.label}${valueLabel_0 ? ":" : ""}`}</span>{pending_0 ? <Skeleton size="xs" shape="pill" className="w-16" /> : valueLabel_0 && <span className="min-w-0 truncate text-text-secondary">{valueLabel_0}</span>}<ChevronDown className="shrink-0" /></Button></StandaloneFilterDropdown></Fragment>;
      };
      $[51] = filterInputWithLabelCache;
      $[52] = handlePositiveChange;
      $[53] = typedCurrentFilters;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    t23 = standaloneFilters.map(t24);
    $[46] = filterInputWithLabelCache;
    $[47] = handlePositiveChange;
    $[48] = standaloneFilters;
    $[49] = typedCurrentFilters;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[55] !== activeClauses.length || $[56] !== addFilterConfigs || $[57] !== filterComponents || $[58] !== filterInputWithLabelCache || $[59] !== handlePositiveChange || $[60] !== hasAddFilterConfigs || $[61] !== isInteractive || $[62] !== showTrigger || $[63] !== triggerLabel || $[64] !== typedCurrentFilters) {
    t24 = showTrigger && hasAddFilterConfigs && <FilterDropdown listConfigFilters={addFilterConfigs as Record<string, AnyFilterConfig>} filterComponents={filterComponents} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved} groupSearchResults={true}>{triggerLabel ? <Button variant="tertiary" size="lg" iconOnlyBelowMd={true} type="button" aria-label={activeClauses.length > 0 ? `Add filter, ${activeClauses.length} active` : "Add filter"} tooltip="Add filter" disabled={!isInteractive}><ListFilterIcon /><span>{triggerLabel}</span>{activeClauses.length > 0 && <span className="list-filter-trigger-count" aria-hidden="true">{activeClauses.length}</span>}</Button> : <IconButton label={activeClauses.length > 0 ? `Add filter, ${activeClauses.length} active` : "Add filter"} variant="toolbar" size={32} disabled={!isInteractive}><ListFilterIcon /></IconButton>}</FilterDropdown>;
    $[55] = activeClauses.length;
    $[56] = addFilterConfigs;
    $[57] = filterComponents;
    $[58] = filterInputWithLabelCache;
    $[59] = handlePositiveChange;
    $[60] = hasAddFilterConfigs;
    $[61] = isInteractive;
    $[62] = showTrigger;
    $[63] = triggerLabel;
    $[64] = typedCurrentFilters;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== activeClauses || $[67] !== addFilterConfigs || $[68] !== clearActiveClauses || $[69] !== filterComponents || $[70] !== filterConfigs || $[71] !== filterInputWithLabelCache || $[72] !== handlePositiveChange || $[73] !== hasAddFilterConfigs || $[74] !== isInteractive || $[75] !== onFilterChange || $[76] !== optionMarkCache || $[77] !== showActiveClauses || $[78] !== typedCurrentFilters) {
    t25 = showActiveClauses && activeClauses.length > 0 && <FilterBar add={hasAddFilterConfigs && <FilterDropdown listConfigFilters={addFilterConfigs as Record<string, AnyFilterConfig>} filterComponents={filterComponents} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved} groupSearchResults={true}><FilterBarAdd disabled={!isInteractive} /></FilterDropdown>} clear={<FilterBarClear aria-label="Clear filters" disabled={!isInteractive} onClick={clearActiveClauses} />}>{activeClauses.map(clause_1 => {
        const filterConfig_2 = filterConfigs[clause_1.filterKey];
        const filterComponent_2 = filterComponents[clause_1.filterKey];
        if (!filterConfig_2 || !filterComponent_2) {
          return null;
        }
        const displayValues = clause_1.filterKey === "season" ? [...new Map(clause_1.values.map(_temp4)).values()] : clause_1.values;
        const pending_1 = displayValues.some(_temp5);
        const fullValueLabel = displayValues.filter(_temp6).map(_temp7).join(", ");
        const valueLabel_1 = displayValues.length > 1 ? `${displayValues.length} ${pluralizeFilterNoun(clause_1.label)}` : fullValueLabel;
        const operatorLabel = getFilterOperatorLabel(filterConfig_2, clause_1.values.length, clause_1.operator);
        const markedValue = displayValues.length === 1 ? displayValues[0] : undefined;
        const valueMark = markedValue && (markedValue.tone || markedValue.icon ? markedValue : optionMarkCache[clause_1.filterKey]?.[String(markedValue.value)]);
        const editValues = clause_1.operator === "isNot" ? {
          ...typedCurrentFilters,
          [clause_1.filterKey]: getFilterExclusions(typedCurrentFilters)[clause_1.filterKey]
        } : typedCurrentFilters;
        return <FilterChip key={clause_1.key}><FilterChipField>{clause_1.label}</FilterChipField><FilterChipOperator aria-label={`Set ${clause_1.label} operator to ${clause_1.operator === "is" ? "is not" : "is"}`} tooltip="Change filter operator" disabled={!isInteractive} onClick={() => {
            const next_0 = moveFilterOperator(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, clause_1.operator === "is" ? "isNot" : "is");
            onFilterChange?.(next_0 as $Filters);
          }}>{operatorLabel}</FilterChipOperator><StandaloneFilterDropdown filterKey={clause_1.filterKey} filterConfig={filterConfig_2} filterComponent={filterComponent_2} filterInput={filterInputWithLabelCache} currentValues={editValues} onValuesChange={values_0 => {
            onFilterChange?.(setFilterCondition(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, values_0[clause_1.filterKey]) as $Filters);
          }} onOptionsResolved={handleOptionsResolved}><FilterChipValue aria-label={`Edit ${clause_1.label} filter, ${operatorLabel} ${fullValueLabel}`} tooltip={pending_1 ? undefined : fullValueLabel} disabled={!isInteractive} pending={pending_1} icon={valueMark?.tone ? <StatusDot tone={valueMark.tone} /> : valueMark?.icon}>{valueLabel_1}</FilterChipValue></StandaloneFilterDropdown><FilterChipRemove label={`Remove ${clause_1.label} filter`} disabled={!isInteractive} onClick={() => {
            const nextFilters_0 = setFilterCondition(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, undefined);
            onFilterChange?.(nextFilters_0 as $Filters);
          }} /></FilterChip>;
      })}</FilterBar>;
    $[66] = activeClauses;
    $[67] = addFilterConfigs;
    $[68] = clearActiveClauses;
    $[69] = filterComponents;
    $[70] = filterConfigs;
    $[71] = filterInputWithLabelCache;
    $[72] = handlePositiveChange;
    $[73] = hasAddFilterConfigs;
    $[74] = isInteractive;
    $[75] = onFilterChange;
    $[76] = optionMarkCache;
    $[77] = showActiveClauses;
    $[78] = typedCurrentFilters;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== t22 || $[81] !== t23 || $[82] !== t24 || $[83] !== t25) {
    t26 = <>{t22}{t23}{t24}{t25}</>;
    $[80] = t22;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  const controls = t26;
  if (wrapWithParent) {
    return controls;
  }
  let t27;
  if ($[85] !== className) {
    t27 = cn("flex min-w-0 flex-wrap items-center gap-2", className);
    $[85] = className;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== controls || $[88] !== t27) {
    t28 = <div className={t27}>{controls}</div>;
    $[87] = controls;
    $[88] = t27;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  return t28;
}
function _temp7(value_4) {
  return value_4.valueLabel;
}
function _temp6(value_3) {
  return !value_3.pending;
}
function _temp5(value_2) {
  return value_2.pending;
}
function _temp4(value_1) {
  return [value_1.pending ? value_1.key : value_1.valueLabel, value_1];
}
function _temp3(value_0) {
  return String(value_0.value);
}
function _temp2(option_0) {
  return [option_0.value, {
    icon: option_0.icon,
    tone: option_0.tone
  }];
}
function _temp(option) {
  return option.icon != null || option.tone != null;
}